/* eslint-disable no-undef */
import { API_ROOT } from './api-config';
import { createUrlFilterFromObject } from 'src/utilities/Utilities';
import { saveFreshAccessTokenInLocalStorage } from 'src/utilities/msalUtilities';
import { getCurrentDateTimeString } from 'src/utilities/DateUtilities';
import { equalsIgnoreCase } from './StringUtilities';
import { reloadPage } from 'src/utilities/BrowserUtilities';

const AUTH_RELOAD_COOLDOWN_MS = 10000;
let lastAuthReloadAt = -AUTH_RELOAD_COOLDOWN_MS;

const reloadOnAuthFailure = () => {
  const now = Date.now();
  if (now - lastAuthReloadAt < AUTH_RELOAD_COOLDOWN_MS) return;
  lastAuthReloadAt = now;
  reloadPage();
};

const getAccessToken = async () => {
  const freshAccessToken = await saveFreshAccessTokenInLocalStorage();
  if (freshAccessToken) {
    return freshAccessToken;
  }

  const storedToken = localStorage.getItem('auth');
  if (!storedToken) {
    reloadOnAuthFailure();
  }
  return storedToken;
};

const AUTH_RECOVERY_EVENT = 'lantern:auth-expired';
const AUTH_RECOVERY_COOLDOWN_MS = 30000;
let lastAuthRecoveryAt = 0;

const triggerAuthRecovery = (reason = 'unknown') => {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  if (now - lastAuthRecoveryAt < AUTH_RECOVERY_COOLDOWN_MS) return;
  lastAuthRecoveryAt = now;

  try {
    window.dispatchEvent(new CustomEvent(AUTH_RECOVERY_EVENT, { detail: { reason } }));
  } catch (_error) {
    // best effort only
  }
};

const getResponse = async (response) => {
  let responseBody = response.body;

  if (responseBody) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      responseBody = await response.json();
    } else if (
      contentType.includes('application/vnd.') ||
      contentType.includes('application/octet-stream') ||
      contentType.includes('application/msword') ||
      contentType.includes('application/pdf') ||
      contentType.startsWith('image/')
    ) {
      responseBody = await response.blob();
    } else {
      responseBody = await response.text();
    }
  }

  if (response.status === 401) {
    localStorage.removeItem('auth');
    triggerAuthRecovery('http-401');
  }

  return {
    success: response.ok,
    body: responseBody,
    response: response,
    errorMessage: !response.ok ? responseBody || response.statusText : response.statusText,
  };
};

const executePostRequest = async (url, entity) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    });

    return fetch(url, {
      method: 'POST',
      headers,
      body: entity ? JSON.stringify(entity) : null,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        return getResponse(response);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return null;
};

const executePostRequestWithFormData = async (url, formData) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    const headers = new Headers({
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
    });

    return fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        return getResponse(response);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return null;
};

export const executePostRequestWithJsonBody = async (url, payload) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    });

    return fetch(url, {
      method: 'POST',
      headers,
      body: payload ? JSON.stringify(payload) : null,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        return getResponse(response);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return null;
};

const executeGetRequest = async (url, sort = '') => {
  const accessToken = await getAccessToken();
  const getUrl = sort ? `${url}&sort=${sort}` : url;

  if (accessToken) {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    });

    return fetch(getUrl, {
      method: 'GET',
      headers,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        return getResponse(response);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return null;
};

const executeDeleteRequest = async (url, entity) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    });

    return fetch(url, {
      method: 'DELETE',
      headers,
      body: entity ? JSON.stringify(entity) : null,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        return getResponse(response);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return false;
};

const executePutRequest = async (url, entity) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    });

    return fetch(url, {
      method: 'PUT',
      headers,
      body: entity ? JSON.stringify(entity) : null,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        return getResponse(response);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return false;
};

const executePutRequestWithHeaders = async (url, entity, extraHeaders = {}) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...extraHeaders,
    });

    return fetch(url, {
      method: 'PUT',
      headers,
      body: entity ? JSON.stringify(entity) : null,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        return getResponse(response);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return false;
};

const executePatchRequestWithJsonBody = async (url, entity) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    });

    return fetch(url, {
      method: 'PATCH',
      headers,
      body: entity ? JSON.stringify(entity) : null,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        return getResponse(response);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return false;
};

const executePatchRequest = async (url, entity) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json-patch+json',
      Authorization: `Bearer ${accessToken}`,
    });

    return fetch(url, {
      method: 'PATCH',
      headers,
      body: entity ? JSON.stringify(entity) : null,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        return getResponse(response);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return false;
};

const executeGetRequestForBlob = async (url, fileName) => {
  const accessToken = await getAccessToken();

  if (accessToken) {
    const headers = new Headers({
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
    });

    return fetch(url, {
      method: 'GET',
      headers,
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) {
          console.error(response.statusText);
        }

        if (response.ok) {
          return response.blob().then((blob) => ({
            success: true,
            body: blob,
            response,
            errorMessage: response.statusText,
          }));
        }

        return getResponse(response);
      })
      .then((response) => {
        if (response.success) {
          const element = document.createElement('a');
          element.href = URL.createObjectURL(response.body);
          element.download = fileName;
          document.body.appendChild(element);
          element.click();
        }

        return response;
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  return null;
};

const executePostRequestForBlob = async (url, fileName, entity) => {
  const accessToken = await getAccessToken();
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  });

  return fetch(url, {
    method: 'POST',
    headers,
    body: entity ? JSON.stringify(entity) : '',
    mode: 'cors',
  })
    .then((response) => {
      if (!response.ok) {
        console.error(response.statusText);
      }

      return getResponse(response);
    })
    .then((response) => {
      if (response.success) {
        const element = document.createElement('a');
        element.href = URL.createObjectURL(response.body);
        element.download = fileName;
        document.body.appendChild(element);
        element.click();
      }

      return response;
    })
    .catch((error) => {
      console.error(error); // eslint-disable-line no-console
    });
};

export const getChildClients = async (filters) => {
  let url;

  if (filters) {
    url = `${API_ROOT}/api/childClients?${createUrlFilterFromObject(filters)}`;
  } else {
    url = `${API_ROOT}/api/childClients`;
  }

  return await executeGetRequest(url, 'name');
};

export const getAllChildClientsWithoutAuthorization = async () => {
  const url = `${API_ROOT}/api/ChildClients/GetAllWithoutAuthorization`;

  return await executeGetRequest(url);
};

export const getAllChildClientsWithAuthorization = async (userId) => {
  let url = `${API_ROOT}/api/ChildClients/GetAllWithAuthorization`;

  if (userId) {
    url += `?userId=${encodeURIComponent(userId)}`;
  }

  return await executeGetRequest(url);
};

// Permission Management v2 endpoints.
// All permission options, including client-retailer, service-type, and Market groups.
export const getAllPermissions = async () => {
  const url = `${API_ROOT}/api/Permissions/GetAllPermissions`;

  return await executeGetRequest(url);
};

// The subset of permission options the calling requester is authorized to grant. When a target
// userId is supplied, Media options are further gated by that user's eligibility to have Media assigned.
export const getPermissionsByUser = async (userId) => {
  let url = `${API_ROOT}/api/Permissions/GetPermissionsByUser`;

  if (userId) {
    url += `?userId=${encodeURIComponent(userId)}`;
  }

  return await executeGetRequest(url);
};

// The users that are members of a given permission (security group).
export const getUsersByPermission = async (securityGroup) => {
  const url = `${API_ROOT}/api/Permissions/GetUsersByPermission?securityGroup=${encodeURIComponent(securityGroup)}`;

  return await executeGetRequest(url);
};

// Internal users eligible to have the given service-type component security group assigned
// (the component's "Users" group + developers/admins/permission managers).
export const getComponentEligibleUsers = async (component) => {
  const url = `${API_ROOT}/api/Permissions/GetComponentEligibleUsers?component=${encodeURIComponent(component)}`;

  return await executeGetRequest(url);
};

export const getChildClientsIncludingInactive = async (filters) => {
  let url;

  if (filters) {
    url = `${API_ROOT}/api/ChildClients/GetAllIncludingInactive?${createUrlFilterFromObject(filters)}`;
  } else {
    url = `${API_ROOT}/api/ChildClients/GetAllIncludingInactive`;
  }

  return await executeGetRequest(url, 'name');
};

export const getParentClients = async () => {
  const url = `${API_ROOT}/api/parentClients?limit=0`;
  return await executeGetRequest(url, 'name');
};

export const getAllParentClientsBasic = async () => {
  const url = `${API_ROOT}/api/ParentClients/GetAllBasic`;

  return await executeGetRequest(url);
};

export const getAllRetailersBasic = async () => {
  const url = `${API_ROOT}/api/Retailers/GetAllBasic`;

  return await executeGetRequest(url);
};

export const createParentClient = async (parentClientBasic) => {
  const url = `${API_ROOT}/api/ParentClients/CreateBasic`;

  return await executePostRequest(url, parentClientBasic);
};

export const createChildClient = async (childClientBasic) => {
  const url = `${API_ROOT}/api/ChildClients/CreateBasic`;

  return await executePostRequest(url, childClientBasic);
};

export const updateParentClientBasic = async (parentClientBasic) => {
  const url = `${API_ROOT}/api/ParentClients/UpdateBasic`;

  return await executePutRequest(url, parentClientBasic);
};

export const updateChildClientBasic = async (childClientBasic) => {
  const url = `${API_ROOT}/api/ChildClients/UpdateBasic`;

  return await executePutRequest(url, childClientBasic);
};

export const getRetailers = async () => {
  const url = `${API_ROOT}/api/Retailers?limit=0`;

  return await executeGetRequest(url);
};

export const getAllRetailersWithoutAuthorization = async () => {
  const url = `${API_ROOT}/api/Retailers/GetAllWithoutAuthorization?limit=0`;

  return await executeGetRequest(url);
};

export const getRetailersByParentClient = async (parentClientId) => {
  const url = `${API_ROOT}/api/ParentClients/${parentClientId}/Retailers`;

  return await executeGetRequest(url);
};

export const getChildClientsBasicByParentClientId = async (parentClientId) => {
  const url = `${API_ROOT}/api/ChildClients/GetChildClientsBasicByParentClientId?parentClientId=${parentClientId}`;

  return await executeGetRequest(url);
};

export const getFeatureFlags = async (flagKeys) => {
  const encodedKeys = flagKeys.map((key) => encodeURIComponent(key)).join(',');

  const url = `${API_ROOT}/api/FeatureFlags?keys=${encodedKeys}`;

  return await executeGetRequest(url);
};

export const setFeatureFlag = async (flagKey, isEnabled) => {
  let url = `${API_ROOT}/api/FeatureFlags?flagKey=${flagKey}&isEnabled=${isEnabled}`;

  return executePutRequest(url);
};

export const getUserAlerts = async () => {
  let url = `${API_ROOT}/api/Alerts`;

  return executeGetRequest(url);
};

export const archiveAlert = async (alertId) => {
  let url = `${API_ROOT}/api/Alerts/${alertId}`;

  return executePutRequest(url);
};

export const unarchiveAlert = async (alertId) => {
  let url = `${API_ROOT}/api/Alerts/${alertId}`;

  return executeDeleteRequest(url);
};

export const getUserExternalUsers = async () => {
  let url = `${API_ROOT}/api/MicrosoftGraph/GetUserExternalUsers`;

  return executeGetRequest(url);
};

export const getUserExternalUsersFromCache = async () => {
  let url = `${API_ROOT}/api/EntraCache/GetUserExternalUsers`;

  return executeGetRequest(url);
};

export const getAllInternalUsers = async (includeUser = true) => {
  let url = `${API_ROOT}/api/MicrosoftGraph/GetAllInternalUsers`;

  if (includeUser) {
    url += '?includeUser=true';
  }

  return executeGetRequest(url);
};

export const searchInternalUsers = async (query) => {
  const url = `${API_ROOT}/api/MicrosoftGraph/SearchInternalUsers?query=${encodeURIComponent(query)}`;
  return executeGetRequest(url);
};

export const getAllInternalUsersFromCache = async (includeUser = true) => {
  let url = `${API_ROOT}/api/EntraCache/GetAllInternalUsers`;

  if (includeUser) {
    url += '?includeUser=true';
  }

  return executeGetRequest(url);
};

export const searchInternalUsersFromCache = async (query) => {
  const url = `${API_ROOT}/api/EntraCache/SearchInternalUsers?query=${encodeURIComponent(query)}`;
  return executeGetRequest(url);
};

export const getAllUserReportsFromCache = async (includeUser = false, childClientId = null) => {
  let url = `${API_ROOT}/api/EntraCache/GetAllUserReports`;

  const params = [];
  if (includeUser) {
    params.push('includeUser=true');
  }
  if (childClientId) {
    params.push(`childClientId=${childClientId}`);
  }

  if (params.length > 0) {
    url += '?' + params.join('&');
  }

  return executeGetRequest(url);
};

export const getMembersOfGrowthTeam365Group = async () => {
  const url = `${API_ROOT}/api/MicrosoftGraph/MembersOfGrowthTeam365Group`;

  return executeGetRequest(url);
};

export const getAllExternalUsers = async () => {
  let url = `${API_ROOT}/api/MicrosoftGraph/GetAllExternalUsers`;

  return executeGetRequest(url);
};

export const getAllExternalUsersFromCache = async () => {
  let url = `${API_ROOT}/api/EntraCache/GetAllExternalUsers`;

  return executeGetRequest(url);
};

export const getTouchpointPlan = async (parentClientId, weekOf, retailerKey) => {
  const params = new URLSearchParams();
  params.set('weekOf', weekOf);
  if (retailerKey) {
    params.set('retailerKey', retailerKey);
  }
  const url = `${API_ROOT}/api/clients/${parentClientId}/touchpoint/plans?${params.toString()}`;
  return executeGetRequest(url);
};

export const saveTouchpointPlan = async (parentClientId, payload, version) => {
  const url = `${API_ROOT}/api/clients/${parentClientId}/touchpoint/plans`;
  const headers = version ? { 'If-Match': version } : {};
  return executePutRequestWithHeaders(url, payload, headers);
};

export const getTouchpointCalendarEvents = async (parentClientId, retailerKey) => {
  const params = new URLSearchParams();
  if (retailerKey) {
    params.set('retailerKey', retailerKey);
  }
  const url = `${API_ROOT}/api/clients/${parentClientId}/touchpoint/calendar?${params.toString()}`;
  return executeGetRequest(url);
};

export const saveTouchpointCalendarEvents = async (parentClientId, retailerKey, keyDates) => {
  const url = `${API_ROOT}/api/clients/${parentClientId}/touchpoint/calendar`;
  const payload = {
    retailerKey,
    keyDates: Array.isArray(keyDates) ? keyDates : [],
  };
  return executePutRequest(url, payload);
};

export const getTouchpointParkingLot = async (parentClientId, retailerKey) => {
  const params = new URLSearchParams();
  if (retailerKey) {
    params.set('retailerKey', retailerKey);
  }
  const url = `${API_ROOT}/api/clients/${parentClientId}/touchpoint/parking-lot?${params.toString()}`;
  return executeGetRequest(url);
};

export const saveTouchpointParkingLot = async (parentClientId, retailerKey, parkingLotItems) => {
  const url = `${API_ROOT}/api/clients/${parentClientId}/touchpoint/parking-lot`;
  const payload = {
    retailerKey,
    items: Array.isArray(parkingLotItems) ? parkingLotItems : [],
  };
  return executePutRequest(url, payload);
};

export const uploadTouchpointAttachment = async (parentClientId, file) => {
  const url = `${API_ROOT}/api/clients/${parentClientId}/touchpoint/attachments`;
  const formData = new FormData();
  formData.append('file', file);
  return executePostRequestWithFormData(url, formData);
};

export const sendNotificationEmail = async (payload) => {
  const url = `${API_ROOT}/api/Notifications/SendEmail`;
  return executePostRequestWithJsonBody(url, payload);
};

export const getExternalUsersByParentClient = async (parentClientId) => {
  let url = `${API_ROOT}/api/MicrosoftGraph/GetExternalUsersByParentClient?parentClientId=${parentClientId}`;

  return executeGetRequest(url);
};

export const getExternalUsersByParentClientFromCache = async (parentClientId) => {
  let url = `${API_ROOT}/api/EntraCache/GetExternalUsersByParentClient?parentClientId=${parentClientId}`;

  return executeGetRequest(url);
};

export const getPaginatedChildClientsByUser = async (userOrId, paginationConfig, userEmail) => {
  let userObjectId = null;
  let resolvedEmail = null;

  if (userOrId && typeof userOrId === 'object') {
    userObjectId = userOrId.graphUserId || userOrId.id || userOrId.userObjectId;
    resolvedEmail = userOrId.userPrincipalName || userOrId.mail || userOrId.email;
  } else if (typeof userOrId === 'string') {
    userObjectId = userOrId;
  }

  if (userEmail) {
    resolvedEmail = userEmail;
  }

  if (!userObjectId && !resolvedEmail) {
    return { success: false, body: 'Missing user id or email' };
  }

  const params = new URLSearchParams();
  if (userObjectId) {
    params.set('userObjectId', userObjectId);
  }
  if (resolvedEmail) {
    params.set('userEmail', resolvedEmail);
  }

  const url = `${API_ROOT}/api/MicrosoftGraph/GetPaginatedUserAuthorizedChildClients?${params.toString()}&${getPaginatedUrlQuery(
    paginationConfig,
  )}`;

  return executeGetRequest(url);
};

export const getPaginatedChildClientsByUserFromCache = async (userOrId, paginationConfig, userEmail) => {
  let userObjectId = null;
  let resolvedEmail = null;

  if (userOrId && typeof userOrId === 'object') {
    userObjectId = userOrId.graphUserId || userOrId.id || userOrId.userObjectId;
    resolvedEmail = userOrId.userPrincipalName || userOrId.mail || userOrId.email;
  } else if (typeof userOrId === 'string') {
    userObjectId = userOrId;
  }

  if (userEmail) {
    resolvedEmail = userEmail;
  }

  if (!userObjectId && !resolvedEmail) {
    return { success: false, body: 'Missing user id or email' };
  }

  const params = new URLSearchParams();
  if (userObjectId) {
    params.set('userObjectId', userObjectId);
  }
  if (resolvedEmail) {
    params.set('userEmail', resolvedEmail);
  }

  const url = `${API_ROOT}/api/EntraCache/GetPaginatedUserAuthorizedChildClients?${params.toString()}&${getPaginatedUrlQuery(
    paginationConfig,
  )}`;

  return executeGetRequest(url);
};

export const getChildClientsByUser = async () => {
  let url = `${API_ROOT}/api/EntraCache/GetUserAuthorizedChildClients`;

  return executeGetRequest(url);
};

export const getParentClientsByUser = async (userOrId, userEmail) => {
  let userObjectId = null;
  let resolvedEmail = null;

  if (userOrId && typeof userOrId === 'object') {
    userObjectId = userOrId.graphUserId || userOrId.id || userOrId.userObjectId;
    resolvedEmail = userOrId.userPrincipalName || userOrId.mail || userOrId.email;
  } else if (typeof userOrId === 'string') {
    userObjectId = userOrId;
  }

  if (userEmail) {
    resolvedEmail = userEmail;
  }

  if (!userObjectId && !resolvedEmail) {
    return { success: false, body: 'Missing user id or email' };
  }

  const params = new URLSearchParams();
  if (userObjectId) {
    params.set('userObjectId', userObjectId);
  }
  if (resolvedEmail) {
    params.set('userEmail', resolvedEmail);
  }

  const url = `${API_ROOT}/api/MicrosoftGraph/GetUserAuthorizedParentClients?${params.toString()}`;
  return executeGetRequest(url, 'name');
};

const getPaginatedUrlQuery = (paginationConfig) => {
  if (paginationConfig?.offset === undefined) {
    paginationConfig.offset = 0;
  }

  if (paginationConfig?.limit === undefined) {
    paginationConfig.limit = 25;
  }

  if (paginationConfig?.sort === undefined) {
    paginationConfig.sort = 'name';
  }

  if (paginationConfig?.sortDirection === undefined) {
    paginationConfig.sortDirection = 'ascending';
  }

  return (
    `offset=${paginationConfig.offset}&` + `limit=${paginationConfig.limit}&` + `sortDirection=${paginationConfig.sortDirection}`
  );
};

export const getInternalUsersByChildClient = async (childClientId) => {
  let url = `${API_ROOT}/api/MicrosoftGraph/GetInternalUsersByChildClient?childClientId=${childClientId}`;

  return executeGetRequest(url);
};

export const getInternalUsersByChildClientFromCache = async (childClientId) => {
  const url = `${API_ROOT}/api/EntraCache/GetInternalUsersByChildClients`;
  const response = await executePostRequest(url, [childClientId]);

  if (!response?.success) {
    return response;
  }

  const usersByClient = response.body || {};
  return {
    ...response,
    body: usersByClient[childClientId] || [],
  };
};

export const getInternalUsersByChildClientsFromCache = async (childClientIds) => {
  const url = `${API_ROOT}/api/EntraCache/GetInternalUsersByChildClients`;

  return executePostRequest(url, childClientIds);
};

export const getPaginatedInternalUsersByChildClient = async (childClientId, paginationConfig) => {
  let url = `${API_ROOT}/api/MicrosoftGraph/GetPaginatedInternalUsersByChildClient?childClientId=${childClientId}&${getPaginatedUrlQuery(paginationConfig)}`;
  return executeGetRequest(url);
};

export const getPaginatedInternalUsersByChildClientFromCache = async (childClientId, paginationConfig) => {
  let url = `${API_ROOT}/api/EntraCache/GetPaginatedInternalUsersByChildClient?childClientId=${childClientId}&${getPaginatedUrlQuery(paginationConfig)}`;
  return executeGetRequest(url);
};

export const getPaginatedExternalUsersByChildClient = async (childClientId, paginationConfig) => {
  let url = `${API_ROOT}/api/MicrosoftGraph/GetPaginatedExternalUsersByChildClient?childClientId=${childClientId}&${getPaginatedUrlQuery(paginationConfig)}`;
  return executeGetRequest(url);
};

export const getPaginatedExternalUsersByChildClientFromCache = async (childClientId, paginationConfig) => {
  let url = `${API_ROOT}/api/EntraCache/GetPaginatedExternalUsersByChildClient?childClientId=${childClientId}&${getPaginatedUrlQuery(paginationConfig)}`;

  return executeGetRequest(url);
};

export const archiveAlertForAllUsers = async (alertId) => {
  let url = `${API_ROOT}/api/Alerts/${alertId}/archiveAlertForAllUsers`;

  return executePutRequest(url);
};

export const updateRetailer = async (id, retailer) => {
  const url = `${API_ROOT}/api/Retailers/${id}`;

  return executePutRequest(url, retailer);
};

export const getSnowflakeTables = async (shouldGetUserFacingTablesOnly) => {
  const url = `${API_ROOT}/api/SnowflakeTableMetadata?shouldGetUserFacingTablesOnly=${shouldGetUserFacingTablesOnly}`;

  return await executeGetRequest(url);
};

export const enqueueFileDownload = async (
  clientId,
  snowflakeTableMetaData,
  selectedRetailersString,
  isAllClientsAction = false,
  soldAt = null,
) => {
  const url = `${API_ROOT}/api/SnowflakeTableMetadata/${snowflakeTableMetaData.downloadEnqueueEndpoint}`;

  const formData = new FormData();

  formData.append('clientId', clientId);
  formData.append('file', null);
  formData.append('snowflakeTableMetadataId', snowflakeTableMetaData.id);
  formData.append('selectedRetailers', selectedRetailersString);
  formData.append('isAllClientsAction', isAllClientsAction);
  formData.append('soldAt', soldAt);

  return executePostRequestWithFormData(url, formData);
};

export const enqueueSnowflakeTableFileUpload = async (clientId, snowflakeTableMetaData, file, isAllClientsAction = false) => {
  const url = `${API_ROOT}/api/SnowflakeTableMetadata/${snowflakeTableMetaData.uploadEnqueueEndpoint}`;

  const formData = new FormData();

  formData.append('clientId', clientId);
  formData.append('file', file);
  formData.append('snowflakeTableMetadataId', snowflakeTableMetaData.id);
  formData.append('isAllClientsAction', isAllClientsAction);

  return executePostRequestWithFormData(url, formData);
};

export const uploadForecastRoundtableFile = async (file, isLockPeriodOpen, selectedFiscalYear) => {
  const url = `${API_ROOT}/api/ForecastRoundtable/UploadFile`;

  const formData = new FormData();
  formData.append('file', file[0]);
  formData.append('isLockPeriodOpen', isLockPeriodOpen);
  formData.append('selectedFiscalYear', selectedFiscalYear.toString());

  return executePostRequestWithFormData(url, formData);
};

export const downloadForecastRoundtableFile = async (netsuiteIds, selectedFiscalYear) => {
  const url = `${API_ROOT}/api/ForecastRoundtable/DownloadFile`;

  const payload = {
    netsuiteIds: netsuiteIds,
    selectedFiscalYear: selectedFiscalYear,
  };

  return executePostRequestWithJsonBody(url, payload);
};

export const getLockedForecastRoundtableClients = async (netsuiteIds) => {
  const url = `${API_ROOT}/api/ForecastRoundtableFileActions/GetLockedForecastRoundtableClients`;

  const payload = {
    netsuiteIds: netsuiteIds,
  };

  return executePostRequestWithJsonBody(url, payload);
};

export const getForecastRoundtableClients = () => {
  const url = `${API_ROOT}/api/ForecastRoundtable/GetForecastRoundtableClients`;

  return executeGetRequest(url);
};

export const getEstimateActualsManagedServiceFiles = async (clientId, year, month) => {
  const url = `${API_ROOT}/api/EstimateActuals/GetEstimateActualsManagedServiceFiles?clientId=${clientId}&year=${year}&month=${month}`;

  return executeGetRequest(url);
};

export const uploadEstimateActualsFile = async (file, clientId, year, month, managedServiceIds) => {
  const url = `${API_ROOT}/api/EstimateActuals/UploadEstimateActualsFile`;
  const formData = new FormData();

  formData.append('file', file);
  formData.append('clientId', clientId);
  formData.append('year', year);
  formData.append('month', month);
  (managedServiceIds || []).forEach((managedServiceId) => {
    formData.append('clientAttributionChildIds', managedServiceId);
  });

  return executePostRequestWithFormData(url, formData);
};

export const downloadEstimateActualsFile = async (uploadId, fileName) => {
  const url = `${API_ROOT}/api/EstimateActuals/DownloadEstimateActualsFile?uploadId=${uploadId}`;

  return executeGetRequestForBlob(url, fileName || `estimate-actuals-${uploadId}`);
};

export const downloadAllEstimateActualsFiles = async (clientId, year, month, fileName) => {
  const url = `${API_ROOT}/api/EstimateActuals/DownloadAllEstimateActualsFiles?clientId=${clientId}&year=${year}&month=${month}`;

  return executeGetRequestForBlob(url, fileName || `actuals-${clientId}-${year}-${month}.zip`);
};

export const getForecastRoundtableContractsMissingNetsuiteId = (parentClientId) => {
  const url = `${API_ROOT}/api/ForecastRoundtable/HasClientContractMissingNetsuiteId?parentClientId=${parentClientId}`;

  return executeGetRequest(url);
};

export const getRevenueNetsuiteClients = () => {
  const url = `${API_ROOT}/api/ClientGoalsTracker/GetRevenueNetsuiteClients`;

  return executeGetRequest(url);
};

export const manageForecastRoundtableFiscalYearConfiguration = async (config, actionType) => {
  const url = `${API_ROOT}/api/ForecastRoundtable/FiscalYearConfiguration?actionType=${actionType}`;

  return executePostRequest(url, config);
};

export const initiateEmberNotificationConfiguration = async (options) => {
  const url = `${API_ROOT}/api/EmberToolEmailNotificationConfigurations/CreateNotificationStatus`;

  return executePostRequest(url, options);
};

export const getEmailNotificationStatus = async (snowflakeTableMetadataId, parentClientId) => {
  const url = `${API_ROOT}/api/EmberToolEmailNotificationConfigurations/GetNotificationStatus?snowflakeTableMetadataId=${snowflakeTableMetadataId}&parentClientId=${parentClientId}`;

  return executeGetRequest(url);
};

export const getAllNotificationStatusForUser = async (snowflakeTableMetadataId) => {
  const url = `${API_ROOT}/api/EmberToolEmailNotificationConfigurations/GetAllNotificationStatusForUser?snowflakeTableMetadataId=${snowflakeTableMetadataId}`;

  return executeGetRequest(url);
};

export const updateOptInStatus = async (snowflakeTableMetadataId, parentClientId, isOptedInToAttributionNotifications) => {
  const url = `${API_ROOT}/api/EmberToolEmailNotificationConfigurations/UpdateNotificationStatus?snowflakeTableMetadataId=${snowflakeTableMetadataId}&parentClientId=${parentClientId}&isOptedInToAttributionNotifications=${isOptedInToAttributionNotifications}`;

  return executePutRequest(url);
};

export const bulkCreateOrUpdateNotificationStatusForUser = async (
  snowflakeTableMetadataId,
  isOptedInToAttributionNotifications,
) => {
  const url = `${API_ROOT}/api/EmberToolEmailNotificationConfigurations/BulkCreateOrUpdateNotificationStatusForUser?snowflakeTableMetadataId=${snowflakeTableMetadataId}&isOptedInToAttributionNotifications=${isOptedInToAttributionNotifications}`;

  return executePostRequest(url);
};

export const bulkCreateUserNotificationStatus = async (requestBody) => {
  const url = `${API_ROOT}/api/EmberToolEmailNotificationConfigurations/BulkCreateUserNotificationStatus`;

  return executePostRequest(url, requestBody);
};

export const addChildClientsToUsers = async (updateUserPermissionsModel) => {
  const url = `${API_ROOT}/api/EntraCache/AddChildClientsToUsers`;

  return executePostRequest(url, updateUserPermissionsModel);
};

export const removeChildClientsFromUsers = async (updateUserPermissionsModel) => {
  const url = `${API_ROOT}/api/EntraCache/RemoveChildClientsFromUsers`;

  return executeDeleteRequest(url, updateUserPermissionsModel);
};

export const refreshUserAssignments = async (payload) => {
  const url = `${API_ROOT}/api/MicrosoftGraph/RefreshUserAssignments`;

  return await executePostRequest(url, payload);
};

export const updateUserPrimaryStatusForChildClient = async (requestBody) => {
  const url = `${API_ROOT}/api/MicrosoftGraph/UpdateUserPrimaryStatusForChildClient`;

  return await executePostRequest(url, requestBody);
};

export const getAllCustomPermissions = async () => {
  const url = `${API_ROOT}/api/CustomPermissions`;

  return executeGetRequest(url);
};

export const getUsersInSecurityGroup = async (securityGroupId) => {
  const url = `${API_ROOT}/api/MicrosoftGraph/GetUsersInSecurityGroup?securityGroup=${securityGroupId}`;

  return executeGetRequest(url);
};

export const getUsersInSecurityGroupFromCache = async (securityGroup) => {
  const url = `${API_ROOT}/api/EntraCache/GetUsersInSecurityGroup?securityGroup=${encodeURIComponent(securityGroup)}`;
  return executeGetRequest(url);
};
export const getPermissionsAudits = async (paginationConfig, startDate, endDate) => {
  let url = `${API_ROOT}/api/PermissionsAudits?offset=${paginationConfig.offset}&limit=${paginationConfig.limit}`;

  if (paginationConfig.sort) {
    url += `&sort=${paginationConfig.sort}`;
  }

  if (paginationConfig.sortDirection) {
    url += `&sortDirection=${paginationConfig.sortDirection}`;
  }

  if (startDate) {
    url += `&startDate=${startDate}`;
  }

  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  return executeGetRequest(url);
};

export const flushActivityLog = async (payload) => {
  const url = `${API_ROOT}/api/Activity/flush`;

  return executePostRequestWithJsonBody(url, payload);
};

export const flushActivityLogKeepalive = (payload) => {
  const url = `${API_ROOT}/api/Activity/flush`;
  const accessToken = localStorage.getItem('auth');

  if (!accessToken) {
    return null;
  }

  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  });

  return fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    mode: 'cors',
    keepalive: true,
  }).catch((error) => {
    console.error(error); // eslint-disable-line no-console
    return null;
  });
};

export const submitUserFeedback = async (payload) => {
  const url = `${API_ROOT}/api/Activity/feedback`;

  return executePostRequestWithJsonBody(url, payload);
};

export const submitIssueReport = async (payload) => {
  const url = `${API_ROOT}/api/Activity/issue-report`;

  return executePostRequestWithFormData(url, payload);
};

export const getParentClientsByRetailerId = async (retailerId) => {
  const url = `${API_ROOT}/api/ParentClients/GetByRetailerId?retailerId=${retailerId}`;

  return executeGetRequest(url);
};

export const getLastUserActionInformation = async (parentClientId, snowflakeTableId) => {
  const url = `${API_ROOT}/api/SnowflakeTableEditorUserActions/GetLatestTableActivityByParentClient?parentClientId=${parentClientId}&snowflakeTableId=${snowflakeTableId}`;

  return executeGetRequest(url);
};

export const getLatestPBCSFileUserActionInfo = async () => {
  const url = `${API_ROOT}/api/PBCSFileUserActions/GetLatestPBCSFileUserActionInfo`;

  return executeGetRequest(url);
};

export const uploadPBCSFile = async (file) => {
  const url = `${API_ROOT}/api/Finance/UploadPBCSExportFile`;
  let lastModified = null;

  if (file.lastModifiedDate) {
    lastModified = file.lastModifiedDate;
  } else {
    lastModified = new Date();
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileLastModifiedDateTime', lastModified.toISOString());

  return executePostRequestWithFormData(url, formData);
};

export const createExternalUser = async (externalUserForm, childClientId) => {
  const url = `${API_ROOT}/api/MicrosoftGraph/CreateExternalUser?childClientId=${childClientId}`;

  return executePostRequest(url, externalUserForm);
};

export const updateExternalUserAccess = async (childClientId, userObjectId, accessUpdate) => {
  const url = `${API_ROOT}/api/Permissions/UpdateExternalUserAccess?childClientId=${childClientId}&userObjectId=${encodeURIComponent(userObjectId)}`;

  return executePostRequest(url, accessUpdate);
};

export const getExternalUserApprovalRequirement = async (childClientId, email) => {
  const encodedEmail = encodeURIComponent(email || '');
  const url = `${API_ROOT}/api/MicrosoftGraph/GetExternalUserApprovalRequirement?childClientId=${childClientId}&email=${encodedEmail}`;

  return executeGetRequest(url);
};

export const searchEntraUsersByName = async (firstName, lastName, isExternal) => {
  let url = `${API_ROOT}/api/EntraCache/SearchUsersByName?firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}`;

  if (isExternal != null) {
    url += `&isExternal=${isExternal}`;
  }

  return executeGetRequest(url);
};

export const getGuestUserRequests = async (status = null) => {
  const queryString = status ? `?status=${encodeURIComponent(status)}` : '';
  const url = `${API_ROOT}/api/GuestUserRequests${queryString}`;

  return executeGetRequest(url);
};

export const approveGuestUserRequest = async (requestId, notes) => {
  const url = `${API_ROOT}/api/GuestUserRequests/${requestId}/approve`;

  return executePostRequest(url, { notes });
};

export const denyGuestUserRequest = async (requestId, notes) => {
  const url = `${API_ROOT}/api/GuestUserRequests/${requestId}/deny`;

  return executePostRequest(url, { notes });
};

export const getClientGoals = async (netsuiteId, fiscalYear) => {
  const url = `${API_ROOT}/api/ClientGoals?netsuiteId=${netsuiteId}&fiscalYear=${fiscalYear}`;
  return executeGetRequest(url);
};

export const getClientGoalById = async (goalId) => {
  const url = `${API_ROOT}/api/ClientGoals/${goalId}`;
  return executeGetRequest(url);
};

export const saveClientGoalsTrackerPage = async (netsuiteId, trackerPage) => {
  const url = `${API_ROOT}/api/ClientGoals?netsuiteId=${netsuiteId}`;
  return executePutRequest(url, trackerPage);
};

export const updateClientGoalsAccountManagerAndTeamLead = async (netsuiteId, fiscalYear, accountManagerName, teamLeadName) => {
  const url = `${API_ROOT}/api/ClientGoals/AccountManagerAndTeamLead?netsuiteId=${netsuiteId}&fiscalYear=${fiscalYear}`;
  return executePatchRequest(url, { accountManagerName, teamLeadName });
};

export const syncClientGoalsToSnowflake = async (netsuiteId, fiscalYear) => {
  const url = `${API_ROOT}/api/ClientGoals/SnowflakeSync?netsuiteId=${netsuiteId}&fiscalYear=${fiscalYear}`;
  return executePostRequest(url, null);
};

export const getClientGoalsClientsList = async () => {
  const url = `${API_ROOT}/api/ClientGoals/Clients`;
  return executeGetRequest(url);
};

export const getLatestClientGoalsSyncAction = async (netsuiteId, fiscalYear) => {
  const url = `${API_ROOT}/api/ClientGoals/SyncActions/Latest?netsuiteId=${netsuiteId}&fiscalYear=${fiscalYear}`;
  return executeGetRequest(url);
};

export const getClientGoalsSyncAction = async (syncId) => {
  const url = `${API_ROOT}/api/ClientGoals/SyncActions/${syncId}`;
  return executeGetRequest(url);
};

export const getUserSettings = async () => {
  const url = `${API_ROOT}/api/UserSettings/GetByUser`;

  return await executeGetRequest(url);
};

export const initializeUserSettings = async () => {
  const url = `${API_ROOT}/api/UserSettings/InitializeForUser`;

  return await executePostRequest(url, null);
};

export const updateUserSettings = async (updatedSettings) => {
  const url = `${API_ROOT}/api/UserSettings/${updatedSettings.id}`;

  return await executePutRequest(url, updatedSettings);
};

export const patchParentClientNotes = async (parentClientId, notes) => {
  const url = `${API_ROOT}/api/ParentClients/${parentClientId}`;

  let patchDocument = [{ op: 'replace', path: '/genAINotes', value: notes }];

  return executePatchRequest(url, patchDocument);
};

export const patchChildClientShouldAllowApiPublish = async (childClientId, shouldAllowApiPublish) => {
  const url = `${API_ROOT}/api/ChildClients/${childClientId}`;

  let patchDocument = [{ op: 'replace', path: '/shouldAllowApiPublish', value: shouldAllowApiPublish }];

  return executePatchRequest(url, patchDocument);
};

export const getFiscalYearConfigurations = async () => {
  const url = `${API_ROOT}/api/ForecastRoundtable/FiscalYearConfigurations`;

  return await executeGetRequest(url);
};

export const getDisplayableFiscalYearConfigurations = async () => {
  const url = `${API_ROOT}/api/ForecastRoundtable/DisplayableFiscalYearConfigurations`;

  return await executeGetRequest(url);
};

export const getClientAttributionParentClientWithClientContracts = async (parentClientId) => {
  const url = `${API_ROOT}/api/ClientAttribution/ParentClientWithClientContracts?foremanParentClientId=${parentClientId}`;

  return await executeGetRequest(url);
};

export const getAllClientAttributionParentClientsWithClientContracts = async () => {
  const url = `${API_ROOT}/api/ClientAttribution/ParentClientsWithClientContracts`;

  return await executeGetRequest(url);
};

export const createClientAttributionParentBrand = async (parentClientId, brandName) => {
  const url = `${API_ROOT}/api/ClientAttribution/ParentClient/${parentClientId}/Brands`;

  return await executePostRequestWithJsonBody(url, { name: brandName });
};

export const deleteClientAttributionParentBrand = async (parentClientId, brandId) => {
  const url = `${API_ROOT}/api/ClientAttribution/ParentClient/${parentClientId}/Brands/${brandId}`;

  return await executeDeleteRequest(url);
};

export const syncMondayForParentClient = async (
  foremanParentClientId,
  { configFileName = null, restoreDeletedObjects = false } = {},
) => {
  const url = `${API_ROOT}/api/MondayManager/sync/${foremanParentClientId}`;
  const body = {};
  if (configFileName) body.configFileName = configFileName;
  body.restoreDeletedObjects = restoreDeletedObjects;

  return await executePostRequest(url, Object.keys(body).length > 0 ? body : null);
};

export const removeTeamsFromBoards = async (foremanParentClientId, teamIds) => {
  const url = `${API_ROOT}/api/MondayManager/teams/remove/${foremanParentClientId}`;

  return await executePostRequest(url, teamIds);
};

export const cleanupMondayBoards = async (foremanParentClientId, boardNames) => {
  const url = `${API_ROOT}/api/MondayManager/boards/cleanup/${foremanParentClientId}`;

  return await executePostRequest(url, { boardNames });
};

export const queueMondayManagedColumnAttach = async (
  managedColumnId,
  templateBoardIds,
  columnTitle = null,
  columnDescription = null,
) => {
  const url = `${API_ROOT}/api/MondayManager/managed-columns/attach`;

  return await executePostRequest(url, {
    managedColumnId,
    templateBoardIds,
    columnTitle,
    columnDescription,
  });
};

export const queueMondayManagedColumnLabelSync = async (managedColumnId) => {
  const url = `${API_ROOT}/api/MondayManager/managed-columns/sync-labels`;

  return await executePostRequest(url, {
    managedColumnId,
  });
};

export const getMondayManagedColumnSetting = async () => {
  const url = `${API_ROOT}/api/MondayManagedColumnSettings`;
  return await executeGetRequest(url);
};

export const updateMondayManagedColumnSetting = async (managedColumnId) => {
  const url = `${API_ROOT}/api/MondayManagedColumnSettings`;
  return await executePutRequest(url, {
    managedColumnId,
  });
};

export const getMondayConfig = async () => {
  const url = `${API_ROOT}/api/MondayConfig`;

  return await executeGetRequest(url);
};

export const listMondayConfigs = async () => {
  const url = `${API_ROOT}/api/MondayConfig/list`;

  return await executeGetRequest(url);
};

export const getMondayConfigByName = async (fileName) => {
  const url = `${API_ROOT}/api/MondayConfig/${encodeURIComponent(fileName)}`;

  return await executeGetRequest(url);
};

export const updateMondayConfig = async (content) => {
  const url = `${API_ROOT}/api/MondayConfig`;

  return await executePostRequest(url, { content });
};

export const updateMondayConfigByName = async (fileName, content) => {
  const url = `${API_ROOT}/api/MondayConfig/${encodeURIComponent(fileName)}`;

  return await executePostRequest(url, { content });
};

export const deleteMondayConfig = async (fileName) => {
  const url = `${API_ROOT}/api/MondayConfig/${encodeURIComponent(fileName)}`;

  return await executeDeleteRequest(url);
};

export const setMondayNewParentDefaultConfig = async (fileName) => {
  const url = `${API_ROOT}/api/MondayConfig/SetNewParentDefault/${encodeURIComponent(fileName)}`;

  return await executePostRequest(url);
};

export const getMondayExceptionWorkspaces = async () => {
  const url = `${API_ROOT}/api/MondayManager/exception-workspaces`;
  return await executeGetRequest(url);
};

export const getMondayWorkspaceAppBootstrap = async (workspaceId) => {
  const url = `${API_ROOT}/api/MondayManager/workspace-app-bootstrap?workspaceId=${encodeURIComponent(workspaceId)}`;
  return await executeGetRequest(url);
};

export const saveMondayExceptionWorkspaces = async (data) => {
  const url = `${API_ROOT}/api/MondayManager/exception-workspaces`;
  return await executePostRequest(url, data);
};

export const getMondayExceptionWorkspaceTemplateGroups = async () => {
  const url = `${API_ROOT}/api/MondayManager/exception-workspace-template-groups`;
  return await executeGetRequest(url);
};

export const saveMondayExceptionWorkspaceTemplateGroups = async (data) => {
  const url = `${API_ROOT}/api/MondayManager/exception-workspace-template-groups`;
  return await executePostRequest(url, data);
};

export const getMondayBoardTemplates = async () => {
  const url = `${API_ROOT}/api/MondayManager/board-templates`;
  return await executeGetRequest(url);
};

export const saveMondayBoardTemplates = async (data) => {
  const url = `${API_ROOT}/api/MondayManager/board-templates`;
  return await executePostRequest(url, data);
};

export const getMondayDocTemplates = async () => {
  const url = `${API_ROOT}/api/MondayManager/doc-templates`;
  return await executeGetRequest(url);
};

export const saveMondayDocTemplates = async (data) => {
  const url = `${API_ROOT}/api/MondayManager/doc-templates`;
  return await executePostRequest(url, data);
};

export const getMondayNightlySyncSettings = async () => {
  const url = `${API_ROOT}/api/MondayNightlySyncSettings`;

  return await executeGetRequest(url);
};

export const updateMondayNightlySyncSettings = async (settings) => {
  const url = `${API_ROOT}/api/MondayNightlySyncSettings`;

  return await executePutRequest(url, settings);
};

export const getMondayNightlySyncEmailSubscribers = async () => {
  const url = `${API_ROOT}/api/monday-nightly-sync/email-subscription`;

  return await executeGetRequest(url);
};

export const updateMondayNightlySyncEmailSubscribers = async (subscriberEmails) => {
  const url = `${API_ROOT}/api/monday-nightly-sync/email-subscription`;

  return await executePutRequest(url, { subscriberEmails });
};

export const getLatestMondayNightlySyncReport = async (batchRunId = null) => {
  const query = batchRunId ? `?batchRunId=${encodeURIComponent(batchRunId)}` : '';
  const url = `${API_ROOT}/api/MondayNightlySyncReport/Latest${query}`;

  return await executeGetRequest(url);
};

export const getClientAttributionAttributeValues = async (excludeNoneValues = false) => {
  const url = `${API_ROOT}/api/ClientAttribution/AttributeValues?excludeNoneValues=${excludeNoneValues}`;

  return await executeGetRequest(url);
};

export const searchClientAttributionParentsByName = async (query) => {
  const url = `${API_ROOT}/api/ClientAttribution/SearchParentClientsByName?query=${encodeURIComponent(query)}`;

  return await executeGetRequest(url);
};

export const getClientAttributionRetailerValues = async () => {
  const url = `${API_ROOT}/api/ClientAttribution/Retailers`;

  return await executeGetRequest(url);
};

export const getClientAttributionServiceTypeValues = async () => {
  const url = `${API_ROOT}/api/ClientAttribution/AttributeValues?excludeNoneValues=true`;
  const response = await executeGetRequest(url);
  const allValues = response?.body || response || {};
  return allValues.serviceTypeValues || allValues.ServiceTypeValues || [];
};

export const getActiveParentClientsBasic = async () => {
  const url = `${API_ROOT}/api/ClientAttribution/GetActiveParentClientsBasic`;

  return await executeGetRequest(url);
};

export const getActiveParentClientsBasicWithNoAttributionParent = async () => {
  const url = `${API_ROOT}/api/ClientAttribution/GetActiveParentClientsBasicWithNoAttributionParent`;

  return await executeGetRequest(url);
};

export const getParentClientConflicts = async (foremanParentClientId) => {
  const url = `${API_ROOT}/api/ClientAttribution/ParentClientConflicts?foremanParentClientId=${foremanParentClientId}`;

  return await executeGetRequest(url);
};

export const initiateClientOnboarding = async (request) => {
  const url = `${API_ROOT}/api/ClientAttribution/InitiateClientOnboarding`;

  return await executePostRequest(url, request);
};

export const updateClientAttributionParentClient = (parentClient, clientConflictIds, originalParentClient = null) => {
  const url = `${API_ROOT}/api/ClientAttribution/ParentClient/${parentClient.foremanParentClientId}`;
  const payload = [
    {
      op: 'replace',
      path: '/hubspotId',
      value: parentClient.hubspotId,
    },
    {
      op: 'replace',
      path: '/lifeStageId',
      value: parentClient.lifeStageId,
    },
    {
      op: 'replace',
      path: '/rmTierId',
      value: parentClient.rmTierId,
    },
    {
      op: 'replace',
      path: '/digitalShelfId',
      value: parentClient.digitalShelfId,
    },
    {
      op: 'replace',
      path: '/classificationId',
      value: parentClient.classificationId,
    },
    {
      op: 'replace',
      path: '/legacyId',
      value: parentClient.legacyId,
    },
    {
      op: 'replace',
      path: '/executiveSponsorId',
      value: parentClient.executiveSponsorId,
    },
    {
      op: 'replace',
      path: '/parentNotes',
      value: parentClient.parentNotes,
    },
    {
      op: 'replace',
      path: '/fiscalStartMonth',
      value: parentClient.fiscalStartMonth,
    },
  ];

  // Only include fields when they actually changed (avoid sending both by default)
  if (originalParentClient) {
    const prevLeadId = originalParentClient.strategicClientLeadId ?? null;
    const currLeadId = parentClient.strategicClientLeadId ?? null;
    if (prevLeadId !== currLeadId) {
      payload.push({ op: 'replace', path: '/strategicClientLeadId', value: currLeadId });
    }

    const prevAdvisorId = originalParentClient.strategicInsightsAdvisorId ?? null;
    const currAdvisorId = parentClient.strategicInsightsAdvisorId ?? null;
    if (prevAdvisorId !== currAdvisorId) {
      payload.push({ op: 'replace', path: '/strategicInsightsAdvisorId', value: currAdvisorId });
    }

    const prevFinanceContactId = originalParentClient.financeContactId ?? null;
    const currFinanceContactId = parentClient.financeContactId ?? null;
    if (prevFinanceContactId !== currFinanceContactId) {
      payload.push({ op: 'replace', path: '/financeContactId', value: currFinanceContactId });
    }

    const prevGrowthLeadEmail = originalParentClient.harvestGrowthLeadEmail;
    const currGrowthLeadEmail = parentClient.harvestGrowthLeadEmail;
    if (!equalsIgnoreCase(prevGrowthLeadEmail, currGrowthLeadEmail)) {
      payload.push({ op: 'replace', path: '/harvestGrowthLeadEmail', value: currGrowthLeadEmail });
    }
  } else {
    // Backward-compatible: if no original provided, include only when set explicitly
    if (typeof parentClient.strategicClientLeadId !== 'undefined') {
      payload.push({ op: 'replace', path: '/strategicClientLeadId', value: parentClient.strategicClientLeadId ?? null });
    }
    if (typeof parentClient.strategicInsightsAdvisorId !== 'undefined') {
      payload.push({
        op: 'replace',
        path: '/strategicInsightsAdvisorId',
        value: parentClient.strategicInsightsAdvisorId ?? null,
      });
    }
    if (typeof parentClient.financeContactId !== 'undefined') {
      payload.push({ op: 'replace', path: '/financeContactId', value: parentClient.financeContactId ?? null });
    }
    if (typeof parentClient.harvestGrowthLeadEmail !== 'undefined') {
      payload.push({ op: 'replace', path: '/harvestGrowthLeadEmail', value: parentClient.harvestGrowthLeadEmail ?? null });
    }
  }

  if (clientConflictIds) {
    payload.push({
      op: 'replace',
      path: '/clientConflictIds',
      value: clientConflictIds,
    });
  }

  return executePatchRequest(url, payload);
};

export const updateClientAttributionClientContract = (clientContract) => {
  const url = `${API_ROOT}/api/ClientAttribution/ClientContract/${clientContract.id}`;
  const payload = [
    {
      op: 'replace',
      path: '/serviceStatus',
      value: clientContract.serviceStatus,
    },
    {
      op: 'replace',
      path: '/subunitId',
      value: clientContract.subunitId,
    },
    {
      op: 'replace',
      path: '/serviceTypeId',
      value: clientContract.serviceTypeId,
    },
    {
      op: 'replace',
      path: '/contractNotes',
      value: clientContract.contractNotes,
    },
    {
      op: 'replace',
      path: '/isNewClient',
      value: false,
    },
  ];

  return executePatchRequest(url, payload);
};

export const updateClientContractRetentionStatusOverride = (clientContractId, retentionStatusOverride) => {
  const url = `${API_ROOT}/api/ClientAttribution/ClientContract/${clientContractId}/RetentionStatusOverride`;

  return executePatchRequestWithJsonBody(url, { retentionStatusOverride });
};

export const createClientAttributionClientContract = async (payload) => {
  const url = `${API_ROOT}/api/ClientAttribution/OnboardClientContract`;
  return executePostRequest(url, payload);
};

export const offboardClientAttributionClientContract = async (payload) => {
  const url = `${API_ROOT}/api/ClientAttribution/OffboardClientContract`;
  return executePostRequest(url, payload);
};

export const bulkUpdateNetsuiteClientMappings = async (mappings, syncToMonday) => {
  const url = `${API_ROOT}/api/ClientAttribution/BulkUpdateNetsuiteClientMappings?syncToMonday=${syncToMonday}`;
  return executePatchRequest(url, mappings);
};

export const getMappingJobStatus = async (jobId) => {
  const url = `${API_ROOT}/api/ClientAttribution/MappingJobStatus/${jobId}`;
  return executeGetRequest(url);
};

export const getClientAttributionChangeHistory = async (foremanParentClientId, paginationConfig, startDate, endDate) => {
  let url = `${API_ROOT}/api/ClientAttribution/ChangeHistory/${foremanParentClientId}?offset=${paginationConfig.offset}&limit=${paginationConfig.limit}`;

  if (startDate) {
    url += `&startDate=${startDate}`;
  }

  if (endDate) {
    url += `&endDate=${endDate}`;
  }

  return executeGetRequest(url);
};

export const downloadClientAttributionParentClients = async (foremanParentClientIds) => {
  let url = `${API_ROOT}/api/ClientAttribution/CreatePBCSImportFileForParentClients`;

  return executePostRequestForBlob(
    url,
    `client_attribution_parent_clients_${getCurrentDateTimeString()}.xlsx`,
    foremanParentClientIds,
  );
};

export const downloadClientAttributionClientContracts = async (contractIds) => {
  let url = `${API_ROOT}/api/ClientAttribution/CreatePBCSImportFileForClientContracts`;

  return executePostRequestForBlob(url, `client_attribution_client_contracts_${getCurrentDateTimeString()}.xlsx`, contractIds);
};

export const downloadHubspotImportFile = async (contractIds) => {
  let url = `${API_ROOT}/api/ClientAttribution/CreateHubspotImportFileForClientContracts`;

  return executePostRequestForBlob(url, `client_attribution_hubspot_${getCurrentDateTimeString()}.xlsx`, contractIds);
};

export const updateClientAttributionControlTable = async (attributeName, recordId, newValue) => {
  const url = `${API_ROOT}/api/ClientAttribution/${attributeName.replace(/\s/g, '')}/${recordId}`;
  const payload = [
    {
      op: 'replace',
      path: '/value',
      value: newValue,
    },
  ];

  return executePatchRequest(url, payload);
};

export const updateExecutiveSponsor = async (id, value) => {
  const url = `${API_ROOT}/api/ClientAttribution/ExecutiveSponsor/${id}`;
  return executePatchRequestWithJsonBody(url, { value });
};

export const updateStrategicClientLead = async (id, value) => {
  const url = `${API_ROOT}/api/ClientAttribution/StrategicClientLead/${id}`;
  return executePatchRequestWithJsonBody(url, { value });
};

export const updateStrategicInsightsAdvisor = async (id, value) => {
  const url = `${API_ROOT}/api/ClientAttribution/StrategicInsightsAdvisor/${id}`;
  return executePatchRequestWithJsonBody(url, { value });
};

export const assignNamedUserToParent = async (foremanParentClientId, namedUserTypeName, displayName) => {
  const url = `${API_ROOT}/api/ClientAttribution/ParentClient/${foremanParentClientId}/${namedUserTypeName.replace(/\s/g, '')}`;
  return executePutRequest(url, { value: displayName ?? null });
};

export const deleteFromClientAttributionControlTable = async (attributeName, recordId) => {
  const url = `${API_ROOT}/api/ClientAttribution/${attributeName.replace(/\s/g, '')}/${recordId}`;

  return executeDeleteRequest(url);
};

export const addToClientAttributionControlTable = async (attributeName, valueToAdd) => {
  const url = `${API_ROOT}/api/ClientAttribution/${attributeName.replace(/\s/g, '')}`;

  return executePostRequest(url, valueToAdd);
};

export const updateClientAttributionControlTableField = async (attributeName, recordId, fieldName, newValue) => {
  const url = `${API_ROOT}/api/ClientAttribution/${attributeName.replace(/\s/g, '')}/${recordId}`;
  const payload = [
    {
      op: 'replace',
      path: `/${fieldName}`,
      value: newValue,
    },
  ];

  return executePatchRequest(url, payload);
};

export const getClientAttributionRetailers = async () => {
  const url = `${API_ROOT}/api/ClientAttribution/Retailers`;

  return executeGetRequest(url);
};

export const getServiceTypeRetailerMappings = async () => {
  const url = `${API_ROOT}/api/ClientAttribution/ServiceTypeRetailerMappings`;

  return executeGetRequest(url);
};

export const updateServiceTypeRetailerMappings = async (serviceTypeId, retailerIds) => {
  const url = `${API_ROOT}/api/ClientAttribution/ServiceTypeRetailerMappings/${serviceTypeId}`;
  const payload = { retailerIds };

  return executePutRequest(url, payload);
};

export const getActiveNetsuiteClients = async () => {
  const url = `${API_ROOT}/api/NetSuiteClientMapping/unmapped`;

  return executeGetRequest(url);
};

export const getUnmappedContracts = async () => {
  const url = `${API_ROOT}/api/ClientAttribution/UnmappedContracts`;

  return executeGetRequest(url);
};

export const getOffboardingRequests = async (filters) => {
  let url = `${API_ROOT}/api/offboarding`;

  if (filters) {
    url = `${url}?${createUrlFilterFromObject(filters)}`;
  }

  return executeGetRequest(url);
};

export const getOffboardingRequest = async (requestId) => {
  const url = `${API_ROOT}/api/offboarding/${requestId}`;

  return executeGetRequest(url);
};

export const cancelOffboardingRequest = async (requestId, cancellationReason) => {
  const url = `${API_ROOT}/api/offboarding/${requestId}/cancel`;
  const payload = { cancellationReason };

  return executePostRequest(url, payload);
};

export const getOffboardingAuditTrail = async (requestId) => {
  const url = `${API_ROOT}/api/offboarding/${requestId}/audit`;

  return executeGetRequest(url);
};

export const getTasksForParent = async (parentClientId) => {
  if (!parentClientId) {
    return { success: true, body: [] };
  }

  const url = `${API_ROOT}/api/UserTask/ForParent?parentClientId=${encodeURIComponent(parentClientId)}`;
  return await executeGetRequest(url);
};

export const createContact = async (contact) => {
  const url = `${API_ROOT}/api/Contacts`;

  return executePostRequest(url, contact);
};

export const getContactsByParentClientId = async (id, parentContactsOnly = false) => {
  const url = `${API_ROOT}/api/Contacts?parentClientId=${id}&parentContactsOnly=${parentContactsOnly}`;

  return executeGetRequest(url);
};

export const deleteContact = async (contactId, parentClientId, notes) => {
  const url = `${API_ROOT}/api/Contacts/${contactId}?parentClientId=${parentClientId}`;

  return executeDeleteRequest(url, { notes });
};

export const updateContact = async (contactId, payload) => {
  const url = `${API_ROOT}/api/Contacts/${contactId}`;

  return executePutRequest(url, payload);
};

// Forecast items/components (client-scoped)
// Pass the builder's scope so the API returns only that retailer + forecast year's plans. Without
// it the API returns every plan for the client, across all retailers and years, with all 52 weeks.
export const getForecastItems = async (parentClientId, { retailerId = null, forecastYear = null } = {}) => {
  const params = new URLSearchParams();
  if (Number.isFinite(Number(retailerId)) && Number(retailerId) > 0) params.set('retailerId', String(Number(retailerId)));
  if (Number.isFinite(Number(forecastYear)) && Number(forecastYear) > 0) params.set('forecastYear', String(Number(forecastYear)));
  const query = params.toString();
  const url = `${API_ROOT}/api/clients/${parentClientId}/items${query ? `?${query}` : ''}`;
  return executeGetRequest(url);
};

export const createForecastItem = async (parentClientId, payload) => {
  const url = `${API_ROOT}/api/clients/${parentClientId}/items`;
  return executePostRequest(url, payload);
};

export const updateForecastItem = async (parentClientId, itemId, payload) => {
  const url = `${API_ROOT}/api/clients/${parentClientId}/items/${itemId}`;
  return executePutRequest(url, payload);
};

export const deleteForecastItem = async (parentClientId, itemId) => {
  const url = `${API_ROOT}/api/clients/${parentClientId}/items/${itemId}`;
  return executeDeleteRequest(url);
};

export const getForecastBuckets = async () => {
  const url = `${API_ROOT}/api/forecast-buckets`;
  return executeGetRequest(url);
};

export const createForecastBucket = async (payload) => {
  const url = `${API_ROOT}/api/forecast-buckets`;
  return executePostRequest(url, payload);
};

export const updateForecastBucket = async (bucketId, payload) => {
  const url = `${API_ROOT}/api/forecast-buckets/${bucketId}`;
  return executePutRequest(url, payload);
};

export const deleteForecastBucket = async (bucketId) => {
  const url = `${API_ROOT}/api/forecast-buckets/${bucketId}`;
  return executeDeleteRequest(url);
};

export const getForecastCycle = async (parentClientId, retailerId, forecastYear) => {
  const url = `${API_ROOT}/api/forecast-cycles?parentClientId=${parentClientId}&retailerId=${retailerId}&forecastYear=${forecastYear}`;
  return executeGetRequest(url);
};

export const establishForecastBase = async (payload) => {
  const url = `${API_ROOT}/api/forecast-cycles/establish-base`;
  return executePostRequest(url, payload);
};

export const markForecastBaselineReviewed = async (parentClientId, retailerId, forecastYear) => {
  const url = `${API_ROOT}/api/forecast-cycles/baseline-reviewed`;
  return executePostRequest(url, { parentClientId, retailerId, forecastYear });
};

export const unestablishForecastBase = async (parentClientId, retailerId, forecastYear) => {
  const url = `${API_ROOT}/api/forecast-cycles/unestablish-base`;
  return executePostRequest(url, { parentClientId, retailerId, forecastYear });
};

export const unlockForecastMonth = async (parentClientId, retailerId, forecastYear, month) => {
  const url = `${API_ROOT}/api/forecast-cycles/unlock-month`;
  return executePostRequest(url, { parentClientId, retailerId, forecastYear, month });
};

/**
 * One scope's fiscal-year anchors: { anchors, yearEnds, retailerAnchors, leadTimeWeeks, supportedYears }.
 *
 * The dates are the retailer's and identical for every client at it: a forecast year opens on the
 * retailer week containing 1 January, whatever the client's lead time. Only leadTimeWeeks belongs to
 * the client, which is why the endpoint still takes the pair. A screen listing many pairs must fetch
 * once per retailer, not once per pair -- see useCachedFiscalCalendars.
 *
 * Dates come back as plain yyyy-MM-dd, not instants -- see parseAnchorDate in FiscalCalendarUtilities.
 */
export const getForecastFiscalAnchors = async (parentClientId, retailerId) => {
  const url =
    `${API_ROOT}/api/forecast-fiscal-anchors` +
    `?parentClientId=${encodeURIComponent(parentClientId)}` +
    `&retailerId=${encodeURIComponent(retailerId)}`;
  return executeGetRequest(url);
};

export const getForecastAdministrationYears = async () => {
  const url = `${API_ROOT}/api/forecast-administration/years`;
  return executeGetRequest(url);
};

export const getForecastAdministrationYearLockState = async (year) => {
  const url = `${API_ROOT}/api/forecast-administration/year-lock-state?year=${year}`;
  return executeGetRequest(url);
};

export const getForecastAdministrationJobs = async (year) => {
  const url = `${API_ROOT}/api/forecast-administration/jobs?year=${year}`;
  return executeGetRequest(url);
};

export const getForecastAdministrationJob = async (jobId) => {
  const url = `${API_ROOT}/api/forecast-administration/jobs/${jobId}`;
  return executeGetRequest(url);
};

export const retryForecastAdministrationJobScope = async (jobId, payload) => {
  const url = `${API_ROOT}/api/forecast-administration/jobs/${jobId}/retry-scope`;
  return executePostRequest(url, payload);
};

export const scheduleForecastAdministrationJob = async (payload) => {
  const url = `${API_ROOT}/api/forecast-administration/jobs`;
  return executePostRequest(url, payload);
};

export const forceFailForecastAdministrationJob = async (jobId) => {
  const url = `${API_ROOT}/api/forecast-administration/jobs/${jobId}/force-fail`;

  return await executePostRequest(url, {});
};

export const cancelForecastAdministrationJob = async (jobId) => {
  const url = `${API_ROOT}/api/forecast-administration/jobs/${jobId}`;
  return executeDeleteRequest(url);
};

export const triggerBaselineRefreshBulk = async (payload) => {
  const url = `${API_ROOT}/api/forecast-administration/baseline-refresh/bulk`;
  return executePostRequest(url, payload);
};

export const getBaselineRefreshBatchStatuses = async (batchIds) => {
  const params = new URLSearchParams();
  batchIds.forEach((batchId) => params.append('batchIds', batchId));
  const url = `${API_ROOT}/api/forecast-administration/baseline-refresh/batches/status?${params.toString()}`;
  return executeGetRequest(url);
};

export const listBaselineRefreshBatches = async (forecastYear, take = 25) => {
  const params = new URLSearchParams();
  if (forecastYear != null) params.append('forecastYear', forecastYear);
  if (take != null) params.append('take', take);
  const url = `${API_ROOT}/api/forecast-administration/baseline-refresh/batches?${params.toString()}`;
  return executeGetRequest(url);
};

export const softDeleteBaselineRefreshBatch = async (batchId) => {
  const url = `${API_ROOT}/api/forecast-administration/baseline-refresh/batches/${batchId}`;
  return executeDeleteRequest(url);
};

export const forceFailBaselineRefreshBatch = async (batchId) => {
  const url = `${API_ROOT}/api/forecast-administration/baseline-refresh/batches/${batchId}/force-fail`;
  return executePostRequest(url);
};

export const getBaselineRefreshStatus = async () => {
  const url = `${API_ROOT}/api/forecast-administration/baseline-refresh/status`;
  return executeGetRequest(url);
};

export const resetForecastScope = async (payload) => {
  const url = `${API_ROOT}/api/forecast-administration/reset-scope`;
  return executePostRequest(url, payload);
};

export const getForecastAdministrationPairStatuses = async (year) => {
  const url = `${API_ROOT}/api/forecast-administration/pair-status?year=${year}`;
  return executeGetRequest(url);
};

export const catchUpForecastAdministrationPair = async (payload) => {
  const url = `${API_ROOT}/api/forecast-administration/catch-up`;
  return executePostRequest(url, payload);
};

export const getForecastCycleSnapshot = async (parentClientId, retailerId, forecastYear, snapshotType, asOfMonth) => {
  const url = `${API_ROOT}/api/forecast-cycles/snapshot?parentClientId=${parentClientId}&retailerId=${retailerId}&forecastYear=${forecastYear}&snapshotType=${snapshotType}${asOfMonth ? `&asOfMonth=${asOfMonth}` : ''}`;
  return executeGetRequest(url);
};

export const refreshWaterfallContext = async ({
  parentClientId,
  retailerId = null,
  forecastYear,
  baselineYear = null,
  clientName = '',
  retailerName = '',
  childClientKey = '',
  waitForCompletion = true,
  timeoutSeconds = 25,
}) => {
  const query = new URLSearchParams({
    waitForCompletion: String(Boolean(waitForCompletion)),
    timeoutSeconds: String(timeoutSeconds),
  });
  const url = `${API_ROOT}/api/waterfall-context/refresh?${query.toString()}`;
  const payload = {
    parentClientId,
    retailerId,
    forecastYear,
    baselineYear,
  };
  if (clientName) payload.clientName = clientName;
  if (retailerName) payload.retailerName = retailerName;
  if (childClientKey) payload.childClientKey = childClientKey;
  return executePostRequest(url, payload);
};

export const getWaterfallContextRefreshStatus = async (jobId) => {
  const url = `${API_ROOT}/api/waterfall-context/status/${jobId}`;
  return executeGetRequest(url);
};

export const getWaterfallContextStagedPreview = async (jobId) => {
  const url = `${API_ROOT}/api/waterfall-context/staged/${jobId}`;
  return executeGetRequest(url);
};

export const applyWaterfallContextStagedImport = async (jobId, payload = {}) => {
  const url = `${API_ROOT}/api/waterfall-context/apply/${jobId}`;
  return executePostRequest(url, payload);
};

export const getWaterfallContextBaselineState = async (parentClientId, retailerId, forecastYear) => {
  const query = new URLSearchParams({
    parentClientId: String(parentClientId),
    retailerId: String(retailerId),
    forecastYear: String(forecastYear),
  });
  const url = `${API_ROOT}/api/waterfall-context/baseline/state?${query.toString()}`;
  return executeGetRequest(url);
};

export const getWaterfallContextBaselineRuns = async (parentClientId, retailerId, forecastYear, take = 20) => {
  const query = new URLSearchParams({
    parentClientId: String(parentClientId),
    retailerId: String(retailerId),
    forecastYear: String(forecastYear),
    take: String(take),
  });
  const url = `${API_ROOT}/api/waterfall-context/baseline/runs?${query.toString()}`;
  return executeGetRequest(url);
};

export const getForecastComponentGroupsByClient = async (parentClientId) => {
  const url = `${API_ROOT}/api/forecast-component-groups/client/${parentClientId}`;
  return executeGetRequest(url);
};

export const createForecastComponentGroup = async (payload) => {
  const url = `${API_ROOT}/api/forecast-component-groups`;
  return executePostRequest(url, payload);
};

export const replaceForecastComponentGroups = async (payload) => {
  const url = `${API_ROOT}/api/forecast-component-groups/replace`;
  return executePostRequest(url, payload);
};

export const commitForecastFactor = async (payload) => {
  const url = `${API_ROOT}/api/forecast-component-groups/commit-factor`;
  return executePostRequest(url, payload);
};

export const updateForecastComponentGroup = async (groupId, payload) => {
  const url = `${API_ROOT}/api/forecast-component-groups/${groupId}`;
  return executePutRequest(url, payload);
};

export const deleteForecastComponentGroup = async (groupId) => {
  const url = `${API_ROOT}/api/forecast-component-groups/${groupId}`;
  return executeDeleteRequest(url);
};

export const getHarvesterExecutionRecords = async (params) => {
  const queryString = new URLSearchParams(params).toString();
  const url = `${API_ROOT}/api/HarvesterStatus?${queryString}`;

  return executeGetRequest(url);
};

export const getHarvesterExecutionRecord = async (executionId) => {
  const url = `${API_ROOT}/api/HarvesterStatus/${executionId}`;

  return executeGetRequest(url);
};

export const getHarvesterStatistics = async () => {
  const url = `${API_ROOT}/api/HarvesterStatus/Statistics`;

  return executeGetRequest(url);
};

export const getHarvesterSummary = async () => {
  const url = `${API_ROOT}/api/HarvesterStatus/Harvesters`;

  return executeGetRequest(url);
};

export const getMondayAccessibleFolders = async (workspaceId, mondayUserId) => {
  const url = `${API_ROOT}/api/MondayExplorer/accessible-folders?workspaceId=${workspaceId}&mondayUserId=${mondayUserId}`;
  return executeGetRequest(url);
};

export const getMondayWorkspaceFolderRegistry = async (workspaceId) => {
  const url = `${API_ROOT}/api/MondayExplorer/workspace-folder-registry?workspaceId=${workspaceId}`;
  return executeGetRequest(url);
};

export const inventoryMondayWorkspace = async (workspaceId) => {
  const url = `${API_ROOT}/api/MondayExplorer/inventory-workspace`;
  return executePostRequest(url, { workspaceId });
};

export const createMondayBoard = async ({
  workspaceId,
  folderId,
  boardName,
  boardKind = null,
  templateId = null,
  isManagedTemplate = false,
  mondayUserId = null,
  isExceptionWorkspace = false,
  skipFolderPermissions = false,
  isPrivateToMe = false,
  queueManagedTemplateCreation = null,
  selectedTeamIds = [],
}) => {
  const url = `${API_ROOT}/api/MondayCreator/create-board`;
  return executePostRequest(url, {
    workspaceId,
    folderId,
    boardName,
    boardKind,
    templateId,
    isManagedTemplate,
    mondayUserId,
    isExceptionWorkspace,
    skipFolderPermissions,
    isPrivateToMe,
    queueManagedTemplateCreation,
    selectedTeamIds,
  });
};

export const createMondayFolder = async ({
  workspaceId,
  parentFolderId,
  folderName,
  mondayUserId = null,
  isExceptionWorkspace = false,
  boardKind = null,
  templateId = null,
  isManagedTemplate = false,
  boardName = null,
  skipFolderPermissions = false,
  isPrivateToMe = false,
  queueManagedTemplateCreation = null,
  selectedTeamIds = [],
}) => {
  const url = `${API_ROOT}/api/MondayCreator/create-folder`;
  return executePostRequest(url, {
    workspaceId,
    parentFolderId,
    folderName,
    mondayUserId,
    isExceptionWorkspace,
    boardKind,
    templateId,
    isManagedTemplate,
    boardName,
    skipFolderPermissions,
    isPrivateToMe,
    queueManagedTemplateCreation,
    selectedTeamIds,
  });
};

export const createMondayDashboard = async (
  workspaceId,
  folderId,
  dashboardName,
  boardKind = 'private',
  mondayUserId = null,
  isExceptionWorkspace = false,
  skipFolderPermissions = false,
  isPrivateToMe = false,
  selectedTeamIds = [],
) => {
  const url = `${API_ROOT}/api/MondayCreator/create-dashboard`;
  return executePostRequest(url, {
    workspaceId,
    folderId,
    dashboardName,
    boardKind,
    mondayUserId,
    isExceptionWorkspace,
    skipFolderPermissions,
    isPrivateToMe,
    selectedTeamIds,
  });
};

export const createMondayDoc = async ({
  workspaceId,
  folderId,
  docName,
  docKind = 'private',
  templateId = null,
  mondayUserId = null,
  isExceptionWorkspace = false,
  skipFolderPermissions = false,
  isPrivateToMe = false,
  selectedTeamIds = [],
}) => {
  const url = `${API_ROOT}/api/MondayCreator/create-doc`;
  return executePostRequest(url, {
    workspaceId,
    folderId,
    docName,
    docKind,
    templateId,
    mondayUserId,
    isExceptionWorkspace,
    skipFolderPermissions,
    isPrivateToMe,
    selectedTeamIds,
  });
};

export const createMondayForm = async ({
  workspaceId,
  folderId,
  formName,
  boardKind = null,
  templateId = null,
  mondayUserId = null,
  isExceptionWorkspace = false,
  skipFolderPermissions = false,
  isPrivateToMe = false,
  selectedTeamIds = [],
}) => {
  const url = `${API_ROOT}/api/MondayCreator/create-form`;
  return executePostRequest(url, {
    workspaceId,
    folderId,
    formName,
    boardKind,
    templateId,
    mondayUserId,
    isExceptionWorkspace,
    skipFolderPermissions,
    isPrivateToMe,
    selectedTeamIds,
  });
};

export const createMondayProject = async ({
  workspaceId,
  folderId,
  projectName,
  boardKind = 'private',
  templateId = null,
  isManagedTemplate = false,
  mondayUserId = null,
  isExceptionWorkspace = false,
  skipFolderPermissions = false,
  isPrivateToMe = false,
  queueManagedTemplateCreation = null,
  selectedTeamIds = [],
}) => {
  const url = `${API_ROOT}/api/MondayCreator/create-project`;
  return executePostRequest(url, {
    workspaceId,
    folderId,
    projectName,
    boardKind,
    templateId,
    isManagedTemplate,
    mondayUserId,
    isExceptionWorkspace,
    skipFolderPermissions,
    isPrivateToMe,
    queueManagedTemplateCreation,
    selectedTeamIds,
  });
};

export const createMondayPortfolio = async (
  workspaceId,
  folderId,
  portfolioName,
  boardKind = 'private',
  mondayUserId = null,
  isExceptionWorkspace = false,
  skipFolderPermissions = false,
  isPrivateToMe = false,
) => {
  const url = `${API_ROOT}/api/MondayCreator/create-portfolio`;
  return executePostRequest(url, {
    workspaceId,
    folderId,
    portfolioName,
    boardKind,
    mondayUserId,
    isExceptionWorkspace,
    skipFolderPermissions,
    isPrivateToMe,
  });
};

export const triggerEntraCacheSync = async (runUserAvatarAfterUserTableSync = false) => {
  const url = `${API_ROOT}/api/EntraCache/TriggerEntraCacheSync?runUserAvatarAfterUserTableSync=${runUserAvatarAfterUserTableSync}`;

  return executePostRequest(url, {});
};

export const downloadPrimaryUsersForAllClients = async () => {
  const url = `${API_ROOT}/api/EntraCache/ExportPrimaryUsersForAllClients`;
  return executeGetRequestForBlob(url, `primary_users_${getCurrentDateTimeString()}.xlsx`);
};

export const getLatestSREFileUpload = async (foremanParentClientId, netsuiteId) => {
  const url = `${API_ROOT}/api/StrategicRetailerEngagement/GetLatestSREFileUpload?foremanParentClientId=${foremanParentClientId}&netsuiteId=${encodeURIComponent(netsuiteId)}`;
  return executeGetRequest(url);
};

export const downloadSREFile = async (foremanParentClientId, blobPath) => {
  const url = `${API_ROOT}/api/StrategicRetailerEngagement/DownloadSREFile?foremanParentClientId=${foremanParentClientId}&blobPath=${encodeURIComponent(blobPath)}`;
  return executeGetRequestForBlob(url, `${blobPath.split('/').pop()}`);
};

export const uploadSREFile = async (file, foremanParentClientId, netsuiteId) => {
  const url = `${API_ROOT}/api/StrategicRetailerEngagement/UploadSREFile`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('foremanParentClientId', foremanParentClientId);
  formData.append('netsuiteId', netsuiteId);
  return executePostRequestWithFormData(url, formData);
};
